import { useState, useEffect, useCallback } from 'react'
import { motion, LayoutGroup } from 'framer-motion'
import { ChatDemo, BibleReaderDemo, PrayerDemo, MinistryMapDemo } from '@/components/demos'

// Maps a product id → demo component. Only products that have a live
// looping demo appear here; the rest fall through to the static image.
// When a card with a demo is expanded, the demo replaces the image AND
// the title/description so the demo gets the full card surface — the
// title is already in the card's collapsed state, so collapsing it on
// expand keeps the layout tight on both mobile and desktop.
const DEMO_FOR = {
  'bible-reader': BibleReaderDemo,
  chat: ChatDemo,
  prayer: PrayerDemo,
  'ministry-map': MinistryMapDemo,
}

const PRODUCTS = [
  {
    id: 'bible-reader',
    title: 'Bible Reader',
    description: 'Read Scripture in 8 translations with AI insight on any verse.',
    image: '/bento/bible-reader.webp',
    status: 'public',
    grid: 'md:col-start-1 md:row-span-2',
  },
  {
    id: 'chat',
    title: 'Chat',
    description: 'Ask one question, hear four voices answer in their own way.',
    image: '/bento/chat.webp',
    status: 'public',
    grid: 'md:col-start-2 md:row-start-1',
  },
  {
    id: 'ministry-map',
    title: 'Ministry Map',
    description: 'Track the people in your life and the next faithful step with each.',
    image: '/bento/ministry-map.webp',
    status: 'public',
    grid: 'md:col-start-3 md:row-start-1',
  },
  {
    id: 'prayer',
    title: 'Prayer',
    description: 'Describe what you’re walking through — get a prayer rooted in Scripture.',
    image: '/bento/prayer.webp',
    status: 'public',
    grid: 'md:col-start-4 md:row-start-1',
  },
  {
    id: 'apologetics-lab',
    title: 'Apologetics Lab',
    description: 'Rehearse hard faith conversations with AI sparring partners.',
    image: '/bento/apologetics-lab.webp',
    status: 'planned',
    grid: 'md:col-start-2 md:row-start-2',
  },
  {
    id: 'sermon-prep',
    title: 'Sermon Prep',
    description: 'Build a sermon — or a whole series — together, step by step.',
    image: '/bento/sermon-prep.webp',
    status: 'planned',
    grid: 'md:col-start-3 md:row-start-2',
  },
  {
    id: 'study-builder',
    title: 'Study Builder',
    description: 'Build a Bible study — or a multi-week series — saved into your Bible.',
    image: '/bento/study-builder.webp',
    status: 'planned',
    grid: 'md:col-start-4 md:row-start-2',
  },
]

const EXPANDED_DETAIL = {
  'bible-reader':
    'Eight translations side by side. Tap any verse for streaming AI insight — Greek and Hebrew, historical context, cross-references, theology, application. Your highlights and notes follow you on every device.',
  chat: 'One question. Four distinct voices answering in parallel — pastoral, apologetic, homiletic, and exegetical — each in its own column. Anchor a passage, layer study lenses, and synthesize them all into one answer.',
  'ministry-map':
    'A private map of the people God has placed in your life. Track where each one is spiritually, what to pray for next, and the most faithful step to take with them this week.',
  prayer:
    'Describe what you’re walking through — anxiety, grief, gratitude, a hard conversation. Get a Scripture-rooted prayer back in seconds, saved to your journal, ready when you need it again.',
  'apologetics-lab':
    "Rehearse hard faith conversations against AI sparring partners — the skeptical coworker, the deconstructing friend, the hostile professor. After each round, get feedback on the methods that work, the citations you could've reached for, and the angles you missed.",
  'sermon-prep':
    "Build a single sermon or a multi-week series with AI, step by step — Big Idea, structure, illustrations, application, and a manuscript scaffold you can actually preach from. Built for pastors who don't have 20 hours a week to prep.",
  'study-builder':
    'Build a single Bible study or a multi-week series with AI, then read it side-by-side with the passage in your Bible Reader. Daily readings, reflection prompts, and a closing prayer for each day — all saved on your account and revisitable anytime.',
}

const COMING_NOTE = {
  'apologetics-lab': 'Currently in development — releasing soon.',
  'sermon-prep': 'Currently in development — releasing soon.',
  'study-builder': 'Currently in development — releasing soon.',
}

// When a card expands, it claims a 2x2 block in place. Cards on column 4
// (Prayer, Study Builder) shift one column to the left so they have room
// to grow without overflowing the grid. Anything that overlaps with this
// new block loses its explicit placement (see isCardDisplaced) and gets
// auto-flowed into a new row that the grid grows into.
const EXPANDED_GRID_CLASS = {
  'bible-reader': 'md:col-start-1 md:row-start-1 md:col-span-2 md:row-span-2',
  chat: 'md:col-start-2 md:row-start-1 md:col-span-2 md:row-span-2',
  'ministry-map': 'md:col-start-3 md:row-start-1 md:col-span-2 md:row-span-2',
  // Prayer expands to a 2-col × 3-row block on the right side. Two
  // cols wide (cols 3-4) matches the other 2×2 expansions; the extra
  // row makes it as tall as three stacked tab rows so the prayer demo
  // can show its busier journal + Shape flow without cramping.
  prayer: 'md:col-start-3 md:row-start-1 md:col-span-2 md:row-span-3',
  'apologetics-lab': 'md:col-start-2 md:row-start-2 md:col-span-2 md:row-span-2',
  'sermon-prep': 'md:col-start-3 md:row-start-2 md:col-span-2 md:row-span-2',
  'study-builder': 'md:col-start-3 md:row-start-2 md:col-span-2 md:row-span-2',
}

// Rect = [colStart, rowStart, colSpan, rowSpan]. Used to detect overlap
// so we know which cards to displace when one expands.
const RECT_ORIGINAL = {
  'bible-reader': [1, 1, 1, 2],
  chat: [2, 1, 1, 1],
  'ministry-map': [3, 1, 1, 1],
  prayer: [4, 1, 1, 1],
  'apologetics-lab': [2, 2, 1, 1],
  'sermon-prep': [3, 2, 1, 1],
  'study-builder': [4, 2, 1, 1],
}

const RECT_EXPANDED = {
  'bible-reader': [1, 1, 2, 2],
  chat: [2, 1, 2, 2],
  'ministry-map': [3, 1, 2, 2],
  // Prayer claims cols 3-4 across 3 rows tall — 2×3 block on the right.
  prayer: [3, 1, 2, 3],
  'apologetics-lab': [2, 2, 2, 2],
  'sermon-prep': [3, 2, 2, 2],
  'study-builder': [3, 2, 2, 2],
}

function rectsOverlap(a, b) {
  return !(a[0] + a[2] <= b[0] || b[0] + b[2] <= a[0] || a[1] + a[3] <= b[1] || b[1] + b[3] <= a[1])
}

function isCardDisplaced(cardId, expandedId) {
  if (!expandedId || cardId === expandedId) return false
  return rectsOverlap(RECT_ORIGINAL[cardId], RECT_EXPANDED[expandedId])
}

export default function ProductBento() {
  const [expandedId, setExpandedId] = useState(null)

  const toggle = useCallback(id => {
    setExpandedId(prev => (prev === id ? null : id))
  }, [])

  useEffect(() => {
    if (!expandedId) return
    const handler = e => {
      if (e.key === 'Escape') setExpandedId(null)
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [expandedId])

  return (
    <section className="relative overflow-hidden bg-[#0b1229] py-8 md:py-12">
      {/* Soft gold radial wash from top */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(233,195,73,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        {/* Section heading */}
        <motion.div
          className="text-center mb-6 md:mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold leading-[1.1] text-[#dce1ff]">
            What you'll find inside
          </h2>
          <p className="mt-4 text-base md:text-lg text-[#8891b0] max-w-xl mx-auto">
            Multiple tools designed to help you study Scripture, pray, and grow &mdash; built with
            Christians at the center.
          </p>
        </motion.div>

        {/* Bento grid */}
        <LayoutGroup>
          <div
            className="grid grid-cols-1 md:grid-cols-4 grid-flow-row-dense gap-3 md:gap-4"
            style={{ gridAutoRows: 'minmax(180px, auto)' }}
          >
            {PRODUCTS.map((product, idx) => (
              <ProductCard
                key={product.id}
                product={product}
                isExpanded={expandedId === product.id}
                expandedId={expandedId}
                anyExpanded={expandedId !== null}
                onToggle={() => toggle(product.id)}
                index={idx}
              />
            ))}
          </div>
        </LayoutGroup>

        {/* Quiet one-liner below the bento — implies more depth without
            listing it all. Sets the bento as the dominant element on
            the page; the rest is left to discovery once they're signed in. */}
        <p
          className="mt-14 md:mt-16 text-center text-sm md:text-base italic font-heading"
          style={{ color: '#8891b0' }}
        >
          …and more features inside
        </p>
      </div>
    </section>
  )
}

function ProductCard({ product, isExpanded, expandedId, anyExpanded, onToggle, index }) {
  const { id, title, description, image, status, grid } = product
  const isHero = id === 'bible-reader'
  // `heroLayout` controls the OVERSIZED hero presentation (tall image,
  // larger title + description). When the hero is displaced by another
  // expansion, drop the hero treatment so it matches the other cards'
  // natural height and the row doesn't grow oversized below it.
  const heroLayout = isHero && !anyExpanded
  const isPlanned = status === 'planned'
  const isDimmed = anyExpanded && !isExpanded
  const isDisplaced = !isExpanded && anyExpanded && isCardDisplaced(id, expandedId)
  const [isHovered, setIsHovered] = useState(false)
  const Demo = DEMO_FOR[id]
  const hasDemo = !!Demo
  const showDemo = isExpanded && hasDemo

  // When ANY card is expanded with a tall demo, the expanded row grows
  // to fit the demo height. CSS Grid's default `align-items: stretch`
  // would then resize every other card in those rows to match — Mason
  // doesn't want that. Override with `align-self: start` on non-expanded
  // cards so they keep their natural height at the top of the row,
  // while displaced cards still flow into the gap via grid-flow-dense.
  const selfAlign = anyExpanded && !isExpanded ? 'start' : 'stretch'

  // Hero (Bible Reader) drops its `md:row-span-2` when an unrelated card
  // is expanded — otherwise the hero claims a 2-row area in the grid and
  // looks oversized + empty above tall demos. When expanded itself, it
  // uses the dedicated 2x2 EXPANDED_GRID_CLASS so its own demo fits.
  const heroDisplaced = isHero && !isExpanded && anyExpanded

  // Grid placement:
  //  - expanded card  → grows to 2x2 (per EXPANDED_GRID_CLASS map)
  //  - displaced card → drops its explicit placement so the grid auto-flow
  //                     pushes it into a new row below
  //  - hero, sibling expanded → drop `row-span-2` so it doesn't claim a
  //                     2-row area of the now-taller expanded grid
  //  - everyone else  → keeps original placement
  const gridClasses = isExpanded
    ? EXPANDED_GRID_CLASS[id] || ''
    : isDisplaced
      ? ''
      : heroDisplaced
        ? 'md:col-start-1 md:row-start-1'
        : grid || ''

  // Image container — expanded gets a wider, more dramatic aspect.
  // The hero (Bible Reader) gets a tall vertical image ONLY when nothing
  // else is expanded; once a sibling card expands, the hero drops the
  // hero treatment and uses the regular aspect-[3/2] so its card height
  // matches the others in the row.
  const imageBoxClasses = isExpanded
    ? 'w-full aspect-[3/2] flex-shrink-0'
    : heroLayout
      ? 'w-full aspect-[3/2] md:aspect-auto md:flex-1 md:min-h-[180px]'
      : 'w-full aspect-[3/2]'

  const handleKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onToggle()
    }
  }

  return (
    <motion.div
      layout
      transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      animate={{ opacity: isDimmed ? 0.45 : 1 }}
      className={`relative ${gridClasses}`}
      style={{
        alignSelf: selfAlign,
        // When displaced by another card's expansion, lock a FIXED
        // height so every non-expanded card matches exactly. Without
        // this, content-driven differences (BR's description wraps to
        // 3 lines, Chat's to 4) leave one card a few pixels shorter
        // than its neighbors. Fixed height = pixel-perfect uniformity.
        ...(anyExpanded && !isExpanded ? { height: 320 } : {}),
      }}
    >
      <motion.div
        layout
        role="button"
        tabIndex={0}
        whileHover={!isExpanded ? { scale: 1.02 } : undefined}
        // Per-property transition: layout uses no-overshoot ease (so cards
        // that change column-start don't visually "push out" past their
        // target on the way), hover keeps its springy feel.
        transition={{
          layout: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
          default: { type: 'spring', stiffness: 300, damping: 25 },
        }}
        onClick={onToggle}
        onKeyDown={handleKeyDown}
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        aria-label={isPlanned ? `${title} — coming soon` : title}
        aria-expanded={isExpanded}
        style={{
          borderRadius: '1rem',
          backgroundColor: 'rgba(24,30,54,0.6)',
        }}
        className="group relative w-full h-full text-left overflow-hidden focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e9c349] cursor-pointer flex flex-col"
      >
        {/*
         * Border overlay with its OWN layout prop. Framer Motion gives any
         * motion element with `layout` a counter-transform during a parent's
         * FLIP animation, so the border keeps its 1px visual width instead
         * of scaling to ~4px during expand/collapse. Sits above everything
         * via z-index, ignores pointer events so clicks pass through.
         */}
        <motion.div
          layout
          aria-hidden="true"
          animate={{
            // Brighter gold border when the card is either hovered OR fully
            // expanded — the expanded state stays "active-looking" instead
            // of dropping back to the faint default.
            borderColor: isExpanded || isHovered ? 'rgba(233,195,73,0.4)' : 'rgba(233,195,73,0.15)',
          }}
          transition={{
            layout: { duration: 0.45, ease: [0.4, 0, 0.2, 1] },
            borderColor: { duration: 0.2 },
          }}
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '1rem',
            borderWidth: '1px',
            borderStyle: 'solid',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        />

        {/* Image OR live demo when expanded */}
        {showDemo ? (
          // Demo replaces the image entirely when this card is expanded.
          // The demo's own internal container drives height; the outer
          // bento card grows with it via `grid-flow-row-dense` + auto-rows.
          <motion.div layout className="relative w-full overflow-hidden">
            <Demo />
          </motion.div>
        ) : (
          <motion.div layout className={`relative overflow-hidden ${imageBoxClasses}`}>
            <img
              src={image}
              alt=""
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
            {isPlanned && (
              <span
                className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-[0.1em] backdrop-blur-sm"
                style={{
                  background: 'rgba(11,18,41,0.65)',
                  border: '1px solid rgba(233,195,73,0.4)',
                  color: '#e9c349',
                }}
              >
                Soon
              </span>
            )}
            {/* Persistent click affordance — visible at rest AND on mobile
                (the card's other cues are hover-only, which is why people
                weren't clicking). Demo cards get a "See it live" pill that
                doubles as a value prop; planned cards get an expand icon. */}
            {!isExpanded &&
              (hasDemo ? (
                <span
                  aria-hidden="true"
                  className="absolute bottom-3 left-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold backdrop-blur-sm pointer-events-none"
                  style={{
                    background: 'rgba(11,18,41,0.7)',
                    border: '1px solid rgba(233,195,73,0.45)',
                    color: '#e9c349',
                  }}
                >
                  <svg
                    width="8"
                    height="9"
                    viewBox="0 0 8 9"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M0 0.5L8 4.5L0 8.5Z" />
                  </svg>
                  See it live
                </span>
              ) : (
                <span
                  aria-hidden="true"
                  className="absolute bottom-3 left-3 inline-flex items-center justify-center w-7 h-7 rounded-full backdrop-blur-sm pointer-events-none"
                  style={{
                    background: 'rgba(11,18,41,0.7)',
                    border: '1px solid rgba(233,195,73,0.3)',
                    color: '#e9c349',
                  }}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4.5 1.5H1.5V4.5M7.5 1.5H10.5V4.5M4.5 10.5H1.5V7.5M7.5 10.5H10.5V7.5" />
                  </svg>
                </span>
              ))}
          </motion.div>
        )}

        {/* Hover glow */}
        <div
          className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{
            background:
              'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(233,195,73,0.08) 0%, transparent 70%)',
          }}
        />

        {/* Text content — when the expanded card is showing a live demo,
            collapse the title/description and just show a compact CTA strip
            beneath the demo. The demo itself carries the product identity. */}
        {showDemo ? (
          <motion.div
            layout
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.25 }}
            className="relative p-4 md:p-5 flex items-center justify-between gap-3 flex-wrap"
          >
            <span className="font-heading font-bold text-[#dce1ff] text-lg md:text-xl">
              {title}
            </span>
            <div className="flex items-center gap-3">
              <a
                href="/login?mode=signup"
                onClick={e => e.stopPropagation()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm bg-[#e9c349] text-[#3c2f00] hover:bg-[#c9a836] transition-all duration-200 hover:scale-105"
              >
                Try Free
                <span aria-hidden>→</span>
              </a>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation()
                  onToggle()
                }}
                className="text-xs uppercase tracking-widest text-[#8891b0] hover:text-[#dce1ff] transition-colors"
                aria-label="Close"
              >
                Close
              </button>
            </div>
          </motion.div>
        ) : (
          <motion.div
            layout
            className={`relative p-4 md:p-5 flex flex-col ${
              isExpanded ? 'flex-1 md:p-8 md:justify-center' : ''
            }`}
          >
            <h3
              className={`font-heading font-bold text-[#dce1ff] leading-tight ${
                heroLayout || isExpanded ? 'text-2xl md:text-3xl' : 'text-xl md:text-2xl'
              }`}
            >
              {title}
            </h3>
            <div className="h-px w-12 my-2.5" style={{ backgroundColor: 'rgba(233,195,73,0.6)' }} />
            <p
              className={`text-[#8891b0] leading-relaxed ${
                heroLayout || isExpanded ? 'text-base md:text-lg' : 'text-sm md:text-base'
              }`}
            >
              {isExpanded ? EXPANDED_DETAIL[id] || description : description}
            </p>

            {isExpanded && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18, duration: 0.25 }}
                className="mt-5 flex flex-col gap-3"
              >
                {isPlanned && COMING_NOTE[id] && (
                  <p
                    className="italic text-sm text-[#8891b0]"
                    style={{ fontFamily: 'Newsreader, Georgia, serif' }}
                  >
                    {COMING_NOTE[id]}
                  </p>
                )}
                <div className="flex items-center gap-4 flex-wrap">
                  <a
                    href="/login?mode=signup"
                    onClick={e => e.stopPropagation()}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-200 hover:scale-105 ${
                      isPlanned
                        ? 'border border-[#e9c349] text-[#e9c349] bg-transparent hover:bg-[rgba(233,195,73,0.1)]'
                        : 'bg-[#e9c349] text-[#3c2f00] hover:bg-[#c9a836]'
                    }`}
                  >
                    {isPlanned ? 'Get notified' : 'Try Free'}
                    <span aria-hidden>→</span>
                  </a>
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation()
                      onToggle()
                    }}
                    className="text-xs uppercase tracking-widest text-[#8891b0] hover:text-[#dce1ff] transition-colors"
                    aria-label="Close"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        )}
      </motion.div>
    </motion.div>
  )
}
